import {
  ConditionLogicConfigurationCollection,
  ConditionLogicId,
  ICampaignEntryContext,
  IConditionLogicConfiguration,
  IConditionLogicImplementation,
  ITemplateScopeData,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { asyncForEach, toJSON } from '@principle-theorem/shared';
import { compact } from 'lodash';
import {
  AppointmentConfirmedCondition,
  AppointmentExcludesPractitionerCondition,
  AppointmentExcludesTreatmentCategoryCondition,
  AppointmentExcludesTreatmentCondition,
  AppointmentHasTagCondition,
  AppointmentIncludesPractitionerCondition,
  AppointmentIncludesTreatmentCategoryCondition,
  AppointmentIncludesTreatmentCondition,
  AppointmentIsStatusCondition,
  IsFirstAppointmentCondition,
} from './appointment-conditions';
import { AppointmentDepositIsUnpaidCondition } from './deposit-conditions';
import { AlwaysCondition, NeverCondition } from './generic-conditions';
import {
  InvoiceIsOverdueCondition,
  InvoiceIsPaidCondition,
} from './invoice-conditions';
import {
  PatientHasEmailAddressCondition,
  PatientHasIssuedFormCondition,
  PatientHasMobileNumberCondition,
  PatientHasSubmittedFormCondition,
  PatientHasTagCondition,
} from './patient-conditions';

export class ConditionHandler<Context> {
  constructor(
    private _implementations: IConditionLogicImplementation<Context, unknown>[]
  ) {}

  async getNestedSummaries(
    collection: ConditionLogicConfigurationCollection
  ): Promise<string[][]> {
    return asyncForEach(collection.or, async (conditions) => {
      const descriptions = await asyncForEach(conditions.and, (config) =>
        this.getSummary(config)
      );
      return compact(descriptions);
    });
  }

  async getSummary(
    config: IConditionLogicConfiguration<unknown>
  ): Promise<string | undefined> {
    const condition = this._getHandlerById(config.conditionId);
    if (!condition) {
      // eslint-disable-next-line no-console
      console.warn(`Unknown conditionId: ${config.conditionId}`);
      return;
    }
    return condition.getSummary(config.config);
  }

  async passes(
    collection: ConditionLogicConfigurationCollection,
    context: unknown
  ): Promise<boolean> {
    return (
      await asyncForEach(collection.or, async (inner) =>
        (
          await asyncForEach(inner.and, (condition) =>
            this._conditionPasses(condition, context)
          )
        ).every((passes) => passes)
      )
    ).some((passes) => passes);
  }

  async passesForCampaignEntry(
    collection: ConditionLogicConfigurationCollection,
    entryContext: ICampaignEntryContext
  ): Promise<boolean> {
    return (
      await asyncForEach(collection.or, async (inner) =>
        (
          await asyncForEach(inner.and, (condition) =>
            this._conditionPassesForEntry(condition, entryContext)
          )
        ).every((passes) => passes)
      )
    ).some((passes) => passes);
  }

  private async _conditionPasses<Config>(
    config: IConditionLogicConfiguration<Config>,
    context: unknown
  ): Promise<boolean> {
    const condition = this._getHandlerById(config.conditionId);
    if (!condition) {
      // eslint-disable-next-line no-console
      console.warn(`Unknown conditionId: ${config.conditionId}`);
      return false;
    }
    if (!condition.isValidContext(context)) {
      // eslint-disable-next-line no-console
      console.warn(
        `Invalid context for conditionId: ${config.conditionId}`,
        toJSON(context)
      );
      return false;
    }
    return condition.isTrue(config.config, context);
  }

  private async _conditionPassesForEntry<Config>(
    config: IConditionLogicConfiguration<Config>,
    entryContext: ICampaignEntryContext
  ): Promise<boolean> {
    const condition = this._getHandlerById(config.conditionId);
    if (!condition) {
      // eslint-disable-next-line no-console
      console.warn(
        `No condition handler found for ${config.conditionId}, treating as passed`
      );
      return true;
    }
    if (!condition.checkCampaignEntry) {
      return true;
    }
    return condition.checkCampaignEntry(config.config, entryContext);
  }

  private _getHandlerById(
    conditionId: ConditionLogicId
  ): IConditionLogicImplementation<Context, unknown> | undefined {
    return this._implementations.find(
      (implementation) => implementation.conditionId === conditionId
    );
  }
}

const defaultConditions = [ConditionLogicId.Never, ConditionLogicId.Always];

const patientScopedConditions = [
  ConditionLogicId.PatientHasTag,
  ConditionLogicId.PatientHasMobileNumber,
  ConditionLogicId.PatientHasEmailAddress,
  ConditionLogicId.PatientHasIssuedForm,
  ConditionLogicId.PatientHasSubmittedForm,
];

// Verify these for Marketing
export const TEMPLATE_SCOPE_CONDITION_ID_MAP: Record<
  TemplateScope,
  ConditionLogicId[]
> = {
  [TemplateScope.None]: defaultConditions,
  [TemplateScope.Patient]: [...defaultConditions, ...patientScopedConditions],
  [TemplateScope.Appointment]: [
    ...defaultConditions,
    ...patientScopedConditions,
    ConditionLogicId.AppointmentHasTag,
    ConditionLogicId.AppointmentIsStatus,
    ConditionLogicId.AppointmentConfirmed,
    ConditionLogicId.IsFirstAppointment,
    ConditionLogicId.AppointmentIncludesTreatment,
    ConditionLogicId.AppointmentExcludesTreatment,
    ConditionLogicId.AppointmentIsTreatmentCategory,
    ConditionLogicId.AppointmentIsNotTreatmentCategory,
    ConditionLogicId.AppointmentIncludesPractitioner,
    ConditionLogicId.AppointmentExcludesPractitioner,
    ConditionLogicId.AppointmentDepositIsUnpaid,
  ],
  [TemplateScope.Invoice]: [
    ...defaultConditions,
    ...patientScopedConditions,
    ConditionLogicId.InvoiceIsPaid,
    ConditionLogicId.InvoiceIsOverdue,
  ],
  [TemplateScope.GapOffer]: [
    ConditionLogicId.Never,
    ConditionLogicId.PatientHasTag,
    ConditionLogicId.PatientHasMobileNumber,
    ConditionLogicId.PatientHasEmailAddress,
    ConditionLogicId.AppointmentHasTag,
    ConditionLogicId.AppointmentIsStatus,
    ConditionLogicId.AppointmentConfirmed,
  ],
  [TemplateScope.TreatmentPlan]: [
    ...defaultConditions,
    ...patientScopedConditions,
  ],
  [TemplateScope.TreatmentStep]: [
    ...defaultConditions,
    ...patientScopedConditions,
  ],
  [TemplateScope.Referral]: [...defaultConditions, ...patientScopedConditions],
};

export function getConditionImplementations(): IConditionLogicImplementation<
  ITemplateScopeData,
  unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
>[] {
  return [
    new NeverCondition(),
    new AlwaysCondition(),
    new PatientHasTagCondition(),
    new PatientHasMobileNumberCondition(),
    new PatientHasEmailAddressCondition(),
    new InvoiceIsPaidCondition(),
    new InvoiceIsOverdueCondition(),
    new PatientHasIssuedFormCondition(),
    new PatientHasSubmittedFormCondition(),
    new AppointmentHasTagCondition(),
    new AppointmentIsStatusCondition(),
    new AppointmentConfirmedCondition(),
    new IsFirstAppointmentCondition(),
    new AppointmentIncludesTreatmentCondition(),
    new AppointmentExcludesTreatmentCondition(),
    new AppointmentIncludesTreatmentCategoryCondition(),
    new AppointmentExcludesTreatmentCategoryCondition(),
    new AppointmentIncludesPractitionerCondition(),
    new AppointmentExcludesPractitionerCondition(),
    new AppointmentDepositIsUnpaidCondition(),
  ];
}

export function getScopedConditions(scope: TemplateScope): ConditionLogicId[] {
  return TEMPLATE_SCOPE_CONDITION_ID_MAP[scope];
}

export function getConditionImplementation(
  conditionId: ConditionLogicId
): IConditionLogicImplementation<ITemplateScopeData, unknown> {
  const result = getConditionImplementations().find(
    (c) => c.conditionId === conditionId
  );
  if (!result) {
    throw new Error(`Condition Not Implemented: ${conditionId}`);
  }
  return result;
}

export function resolveConditionImplementations(
  conditionIds: ConditionLogicId[]
): IConditionLogicImplementation<ITemplateScopeData, unknown>[] {
  return conditionIds.map((conditionId) =>
    getConditionImplementation(conditionId)
  );
}
